import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { User, Phone, Mail, ShieldCheck, KeyRound, Save, Lock, BadgeCheck, CreditCard, Landmark, Store, Globe } from "lucide-react";
import { api, formatApiError, STAFF_PERMISSIONS } from "@/lib/api";
import { LANGS } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

const PERM_LABEL = Object.fromEntries(STAFF_PERMISSIONS.map((p) => [p.key, p.label]));
const ROLE_LABEL = { admin: "Super-admin", agent: "Staff", client: "Client" };

function Card({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2.5">
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-blue-50 text-blue-600"><Icon className="h-4.5 w-4.5" /></span>
        <div>
          <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
          {desc && <p className="text-xs text-slate-500">{desc}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function PaymentSettingsCard() {
  const [s, setS] = useState({ card_enabled: false, transfer_enabled: false, counter_enabled: false, iban: "", bank_name: "", account_holder: "", instructions: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/payment").then(({ data }) => setS((prev) => ({ ...prev, ...(data.settings || {}) }))).catch(() => {});
  }, []);
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/payment", {
        card_enabled: !!s.card_enabled, transfer_enabled: !!s.transfer_enabled, counter_enabled: !!s.counter_enabled,
        iban: s.iban || null, bank_name: s.bank_name || null, account_holder: s.account_holder || null, instructions: s.instructions || null,
      });
      toast.success("Modes de paiement enregistrés");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const Toggle = ({ icon: Icon, label, desc, k }) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3.5 hover:bg-slate-50">
      <Checkbox checked={!!s[k]} onCheckedChange={(c) => set({ [k]: !!c })} data-testid={`pay-toggle-${k}`} className="mt-0.5" />
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-600"><Icon className="h-4 w-4" /></span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-800">{label}</span>
        <span className="block text-xs text-slate-500">{desc}</span>
      </span>
    </label>
  );
  return (
    <Card icon={CreditCard} title="Modes de paiement" desc="Choisissez comment vos clients peuvent régler leurs devis.">
      <form onSubmit={save} className="space-y-4" data-testid="payment-settings-form">
        <div className="grid gap-3">
          <Toggle icon={CreditCard} label="Carte en ligne (Stripe)" desc="Paiement sécurisé par carte bancaire." k="card_enabled" />
          <Toggle icon={Landmark} label="Virement bancaire" desc="Affiche votre IBAN au client." k="transfer_enabled" />
          <Toggle icon={Store} label="Paiement au comptoir" desc="Le client règle lors du retrait." k="counter_enabled" />
        </div>
        {s.transfer_enabled && (
          <div className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50/50 p-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="pay-iban">IBAN</Label>
              <Input id="pay-iban" data-testid="pay-iban-input" value={s.iban || ""} onChange={(e) => set({ iban: e.target.value })} className="mt-1.5 font-mono" placeholder="BE00 0000 0000 0000" />
            </div>
            <div>
              <Label htmlFor="pay-holder">Bénéficiaire</Label>
              <Input id="pay-holder" data-testid="pay-holder-input" value={s.account_holder || ""} onChange={(e) => set({ account_holder: e.target.value })} className="mt-1.5" placeholder="Stukjes Spare Parts" />
            </div>
            <div>
              <Label htmlFor="pay-bank">Banque</Label>
              <Input id="pay-bank" data-testid="pay-bank-input" value={s.bank_name || ""} onChange={(e) => set({ bank_name: e.target.value })} className="mt-1.5" placeholder="Nom de la banque" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="pay-instr">Instructions (optionnel)</Label>
              <Textarea id="pay-instr" data-testid="pay-instructions-input" value={s.instructions || ""} onChange={(e) => set({ instructions: e.target.value })} className="mt-1.5" rows={2} placeholder="ex : Merci d'indiquer le numéro de devis en communication." />
            </div>
          </div>
        )}
        <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-payment-settings-btn">
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Enregistrement..." : "Enregistrer les modes de paiement"}
        </Button>
      </form>
    </Card>
  );
}

export default function Settings() {
  const { user, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const isAdmin = user?.role === "admin";
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [cur, setCur] = useState("");
  const [npw, setNpw] = useState("");
  const [conf, setConf] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/profile", { name, phone });
      updateUser(data.user);
      toast.success(t("settings.profileSaved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (npw !== conf) { toast.error(t("settings.pwMismatch")); return; }
    if (npw.length < 6) { toast.error(t("settings.pw6")); return; }
    setSavingPw(true);
    try {
      await api.put("/profile/password", { current_password: cur, new_password: npw });
      toast.success(t("settings.pwSaved"));
      setCur(""); setNpw(""); setConf("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const isStaff = user?.role === "admin" || user?.role === "agent";

  return (
    <div className="mx-auto max-w-3xl" data-testid="settings-page">
      <h1 className="font-display text-3xl font-bold tracking-tight">{t("settings.title")}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{t("settings.sub")}</p>

      {/* Identity summary */}
      <div className="mt-7 flex items-center gap-4 rounded-xl border border-blue-200 bg-blue-50/40 p-5">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-blue-600 font-display text-xl font-semibold text-white" data-testid="settings-avatar">
          {user?.name?.charAt(0)?.toUpperCase() || "?"}
        </span>
        <div className="min-w-0">
          <p className="truncate font-display text-lg font-semibold text-slate-900">{user?.name}</p>
          <p className="flex items-center gap-1.5 text-sm text-slate-500"><Mail className="h-3.5 w-3.5" /> {user?.email}</p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700" data-testid="settings-role">
          <ShieldCheck className="h-3.5 w-3.5" /> {ROLE_LABEL[user?.role] || user?.role}
        </span>
      </div>

      <div className="mt-6 grid gap-6">
        {/* Language (client only) */}
        {!isStaff && (
          <Card icon={Globe} title={t("settings.lang")} desc={t("settings.langDesc")}>
            <div className="flex flex-wrap gap-2" data-testid="settings-language">
              {LANGS.map((l) => {
                const active = i18n.language === l.code;
                return (
                  <button key={l.code} type="button" data-testid={`settings-lang-${l.code}`}
                    onClick={() => { i18n.changeLanguage(l.code); api.put("/profile/lang", { lang: l.code }).catch(() => {}); }}
                    className={`flex items-center gap-2 rounded-lg border-2 px-3.5 py-2 text-sm font-medium transition-colors ${active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    <span>{l.flag}</span> {l.label}
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Profile */}
        <Card icon={User} title={t("settings.profile")} desc={t("settings.profileDesc")}>
          <form onSubmit={saveProfile} className="space-y-4">
            <div>
              <Label htmlFor="set-name">{t("settings.fullName")}</Label>
              <Input id="set-name" data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="set-phone">{t("settings.phone")}</Label>
              <div className="relative mt-1.5">
                <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="set-phone" data-testid="settings-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-9" placeholder={t("settings.phoneOpt")} />
              </div>
            </div>
            <div>
              <Label>{t("settings.emailLogin")}</Label>
              <div className="mt-1.5 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-500">
                <Lock className="h-3.5 w-3.5" /> {user?.email} <span className="ml-auto text-xs">{t("settings.notEditable")}</span>
              </div>
            </div>
            <Button type="submit" disabled={savingProfile} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">
              <Save className="mr-1.5 h-4 w-4" /> {savingProfile ? t("common.saving") : t("common.save")}
            </Button>
          </form>
        </Card>

        {/* Password */}
        <Card icon={KeyRound} title={t("settings.security")} desc={t("settings.secDesc")}>
          <form onSubmit={savePassword} className="space-y-4">
            <div>
              <Label htmlFor="set-cur">{t("settings.currentPw")}</Label>
              <Input id="set-cur" data-testid="settings-current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5" required />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="set-npw">{t("settings.newPw")}</Label>
                <Input id="set-npw" data-testid="settings-new-password" type="password" minLength={6} value={npw} onChange={(e) => setNpw(e.target.value)} className="mt-1.5" required />
              </div>
              <div>
                <Label htmlFor="set-conf">{t("settings.confirm")}</Label>
                <Input id="set-conf" data-testid="settings-confirm-password" type="password" value={conf} onChange={(e) => setConf(e.target.value)} className="mt-1.5" required />
              </div>
            </div>
            <Button type="submit" disabled={savingPw} variant="outline" className="border-slate-300" data-testid="save-password-btn">
              <KeyRound className="mr-1.5 h-4 w-4" /> {savingPw ? t("settings.changing") : t("settings.changePw")}
            </Button>
          </form>
        </Card>

        {/* Payment modes (admin only) */}
        {isAdmin && <PaymentSettingsCard />}

        {/* Staff access (read-only) */}
        {isStaff && (
          <Card icon={BadgeCheck} title="Mes accès" desc="Modules auxquels vous avez accès (gérés par l'administrateur).">
            <div className="flex flex-wrap gap-1.5" data-testid="settings-permissions">
              {user?.role === "admin" ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"><ShieldCheck className="h-3 w-3" /> Accès complet</span>
              ) : (user?.permissions || []).length === 0 ? (
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">Aucun accès attribué</span>
              ) : (
                (user?.permissions || []).map((p) => (
                  <span key={p} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">{PERM_LABEL[p] || p}</span>
                ))
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
